import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { Alert, Group, Stack, Text } from '@mantine/core';
import { motion } from 'framer-motion';
import { AlertTriangle, ArrowLeft, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';
import { Badge, Button, Card, PageContainer, PageHeader, Section, Skeleton } from '../components/ui';
import { staggerContainer, cardItem } from '../lib/motion';
import { BulkEnrollDialog } from '../components/course/BulkEnrollDialog';
import { CohortMembers } from '../components/course/CohortMembers';
import { CohortAssignments } from '../components/course/CohortAssignments';
import { Announcements } from '../components/course/Announcements';
import { AssignCourse } from '../components/course/AssignCourse';
import { useCohort } from '../hooks/useCohorts';
import type { CohortStatus } from '../api/types';

/**
 * `/admin/cohorts/{id}/enroll` — bulk-enroll trainees into a cohort.
 *
 * Resolves the cohort → looks up the attached courseId → mounts the
 * `BulkEnrollDialog` against that course. The dialog handles CSV upload +
 * paste, server-side parse, and the bulk-enroll API call (Phase 4.5 wires
 * `services/course/handler.py` `bulkEnroll` against `/courses/{slug}/bulk-
 * enroll` today; once the cohort-aware route lands the dialog will accept
 * a `cohortId` prop to scope the enrollment to this cohort specifically).
 *
 * Backend gap noted inline below — the cohort-scoped bulk-enroll route
 * doesn't exist yet; for now we enroll into the cohort's course directly
 * and the cohort↔trainee association will be created server-side once
 * `cohort_enrollments` lands (PLAN.md §Phase 4.5).
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Route = (createFileRoute as any)('/admin/cohorts/$id/enroll')({
  component: AdminCohortEnroll,
});

const STATUS_VARIANT: Record<CohortStatus, 'success' | 'info' | 'warn' | 'danger'> = {
  active: 'success',
  paused: 'warn',
  archived: 'danger',
  draft: 'info',
};

export function AdminCohortEnroll() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { id } = useParams({ strict: false }) as { id: string };
  const { data, isPending, isError, error } = useCohort(id);
  const [dialogOpen, setDialogOpen] = useState(false);

  if (isPending) {
    return (
      <PageContainer size="default">
        <Stack gap="lg">
          <Skeleton height={36} width="40%" />
          <Skeleton height={20} width="60%" />
          <Skeleton height={200} radius="md" />
        </Stack>
      </PageContainer>
    );
  }

  if (isError || !data) {
    return (
      <PageContainer size="default">
        <PageHeader
          icon={UserPlus}
          title="Enroll Trainees"
          description="Add trainees to this cohort."
          breadcrumbs={[
            { label: 'Forge', to: '/' },
            { label: 'Admin', to: '/admin' },
            { label: 'Cohorts', to: '/admin/cohorts' },
            { label: 'Enroll' },
          ]}
        />
        <Stack gap="md">
          <Alert color="red" icon={<AlertTriangle size={16} />} variant="light" title="Could not load cohort">
            {(error as Error)?.message ?? `Cohort not found: ${id}`}
          </Alert>
          <Link to="/admin/cohorts" style={{ textDecoration: 'none' }}>
            <Button variant="ghost" leftIcon={<ArrowLeft size={14} />}>
              Back to cohorts
            </Button>
          </Link>
        </Stack>
      </PageContainer>
    );
  }

  return (
    <PageContainer size="default">
      <PageHeader
        icon={UserPlus}
        title="Enroll Trainees"
        description="Add trainees to this cohort."
        breadcrumbs={[
          { label: 'Forge', to: '/' },
          { label: 'Admin', to: '/admin' },
          { label: 'Cohorts', to: '/admin/cohorts' },
          { label: 'Enroll' },
        ]}
        actions={
          <Button variant="primary" leftIcon={<Users size={14} />} onClick={() => setDialogOpen(true)}>
            Open CSV enroll dialog
          </Button>
        }
      />

      <Section
        title={
          <Group gap="sm">
            <span>{data.name}</span>
            <Badge variant={STATUS_VARIANT[data.status]}>{data.status}</Badge>
          </Group>
        }
        description={
          <>
            Upload or paste a CSV (header <code>email,first_name,last_name</code>) to bulk-enroll
            trainees into <strong>{data.name}</strong>. Existing enrollments are skipped silently.
          </>
        }
      >
        <motion.div variants={staggerContainer} initial="hidden" animate="show">
          <motion.div variants={cardItem}>
            <Card>
              <Stack gap="sm">
                <Group gap="lg">
                  <Metric label="Cohort" value={data.name} />
                  <Metric label="Course" value={data.courseTitle} />
                  <Metric label="Current trainees" value={String(data.traineeCount)} />
                </Group>
                {data.status === 'archived' ? (
                  <Alert color="red" icon={<AlertTriangle size={14} />} variant="light">
                    This cohort is archived. New enrollments will fail until it is reactivated.
                  </Alert>
                ) : null}
                {data.status === 'draft' ? (
                  <Alert color="amber" icon={<AlertTriangle size={14} />} variant="light">
                    This cohort is in draft state. Enrollments will be staged but not visible to
                    instructors until the cohort is published.
                  </Alert>
                ) : null}
                <Group gap="sm">
                  <Button
                    variant="primary"
                    leftIcon={<Users size={14} />}
                    onClick={() => setDialogOpen(true)}
                  >
                    Open CSV enroll dialog
                  </Button>
                </Group>
                <Text size="xs" c="dimmed" maw={720}>
                  Note: the dialog enrolls into the cohort&apos;s course (
                  <code>{data.courseId}</code>) today; cohort-scoped bulk-enroll
                  (`POST /cohorts/{`{id}`}/enroll`) is the Phase 4.5 backend follow-up
                  tracked in <code>docs/PHASE_STATUS.md</code>.
                </Text>
              </Stack>
            </Card>
          </motion.div>
        </motion.div>
      </Section>

      <Section title="Cohort roster" description="Students in this cohort appear in the gradebook and the review queue.">
        <CohortMembers cohort={data} />
      </Section>

      <Section title="Announcements" description="Post updates to the whole cohort — students see them on their dashboard.">
        <Announcements cohortId={id} canPost />
      </Section>

      <Section title="Assigned labs" description="Assign labs with a due date; the cohort report tracks completion, grades, and time against them.">
        <CohortAssignments cohortId={data.id} />
      </Section>

      <AssignCourse cohortId={data.id} />

      <BulkEnrollDialog
        opened={dialogOpen}
        onClose={() => setDialogOpen(false)}
        courseId={data.courseId}
        courseTitle={`${data.name} — ${data.courseTitle}`}
      />
    </PageContainer>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text size="sm" c="rgb(var(--c-txt-bright))" fw={600}>
        {value}
      </Text>
    </Stack>
  );
}
